import { Button, CircularProgress } from '@mui/material';

import useConditionData from 'hooks/useConditionData';
import { Box } from '@mui/system';
import { Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { produce } from 'immer';
import { set, push } from 'object-path';
import {
  getEmptyCondition,
  getEmptyConditionGroup,
  getEmptyConditionGroupGroup,
} from '../../utils';
import ConditionRenderer from './ConditionRenderer';
import { ConditionGroup } from 'api/models/FieldTemplate';
import { AddFieldValues } from '../../AddFieldTemplateDialog';
import { FormikErrors } from 'formik';

interface ConditionEditorProps {
  rootGroup?: ConditionGroup;
  onChange: (conditionGroup: ConditionGroup | undefined) => void;
  disabled?: boolean;
  errors?: FormikErrors<AddFieldValues>;
}

export default function ConditionEditor({
  rootGroup,
  disabled,
  onChange,
  errors,
}: ConditionEditorProps) {
  const { loading, fieldTemplates, formTemplateTypes, patientFields } =
    useConditionData();
  const { t } = useTranslation();

  const onAddCondition = (path: string) => {
    if (!rootGroup) throw new Error('No conditions');
    const propertyPath = `${path}.conditions`;
    const conditions = produce(rootGroup, draft =>
      push(draft, propertyPath, getEmptyCondition()),
    );
    onChange(conditions);
  };

  const onAddConditionGroup = () => {
    if (!rootGroup) return onChange(getEmptyConditionGroupGroup());

    const conditions = produce(rootGroup, draft => {
      set(draft, 'conditions', [
        ...(rootGroup?.conditions || []),
        getEmptyConditionGroup(),
      ]);
    });
    onChange(conditions);
  };

  if (loading) {
    return (
      <Box
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        height={'50px'}
      >
        <CircularProgress sx={{ mb: 2 }} />
      </Box>
    );
  }

  return (
    <>
      {rootGroup && (
        <ConditionRenderer
          onChange={onChange}
          onAddCondition={onAddCondition}
          rootGroup={rootGroup}
          group={rootGroup}
          path={''}
          fieldTemplates={fieldTemplates}
          formTemplateTypes={formTemplateTypes}
          patientFields={patientFields}
          disabled={disabled}
        />
      )}

      <Box sx={{ mt: 1, mr: 1 }} display={'flex'} justifyContent={'end'}>
        <Button onClick={() => onAddConditionGroup()} disabled={disabled}>
          <Add /> {t('dialogs.addField.addConditionParent')}
        </Button>
      </Box>
    </>
  );
}
